/*!
=========================================================
* BirdNET-Pi Public UI - Custom Index Mockup
=========================================================
*/

import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Container,
  Row,
  Col,
  Table,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import CustomAudioPlayer from "components/AudioPlayer/CustomAudioPlayer";
import SpeciesCard from "components/AudioPlayer/SpeciesCard";
import WeatherCard from "components/AudioPlayer/WeatherCard";
import DetectionChart from "components/AudioPlayer/DetectionChart";
import moment from 'moment';

import { Bar } from "react-chartjs-2";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const Index = () => {
  const [dailyStats, setDailyStats] = useState({
    total: 0,
    today: 0,
    lastHour: 0,
    speciesTotal: 0,
    speciesToday: 0,
    lastDetection: "--",
  });

  const [newSpeciesToday, setNewSpeciesToday] = useState([]);
  const [firstDetectionThisYear, setFirstDetectionThisYear] = useState([]);

  const [speciesActivity, setSpeciesActivity] = useState({
    labels: [],
    datasets: [
      {
        label: "Detections",
        backgroundColor: "#5e72e4",
        data: [],
      },
    ],
  });

  const [playersReady, setPlayersReady] = useState(0);
  
  useEffect(() => {
    console.log("!bang Players ready count:", playersReady);
    if (playersReady === 1) {
      CustomAudioPlayer.initAllPlayers();
    }
  }, [playersReady]);
  

  const [latestDetection, setLatestDetection] = useState(null);


  useEffect(() => {
    fetch(`${API_BASE}/detections/overview`)
      .then(res => res.json())
      .then(data => {
        const formattedLastDetection = data.lastDetection
          ? `${moment(data.lastDetection).fromNow()}`
          : "--";
        setDailyStats({ ...data, lastDetection: formattedLastDetection });
      })
      .catch(err => console.error("Error loading overview:", err));
    fetch(`${API_BASE}/detections/newspeciestoday`)
      .then(res => res.json())
      .then(data => setNewSpeciesToday(data))
      .catch(err => console.error("Error loading new species:", err));

    fetch(`${API_BASE}/detections/firstdetectionthisyear`)
      .then(res => res.json())
      .then(data => {
        const today = moment().format("YYYY-MM-DD");
        const returnedToday = data.filter((bird) => bird.firstDate === today);
        setFirstDetectionThisYear(returnedToday);
      })
      .catch(err => console.error("Error loading first detections this year:", err));

    fetch(`${API_BASE}/detections/todaysactivity`)
      .then(res => res.json())
      .then(data => setSpeciesActivity(data))
      .catch(err => console.error("Error loading activity:", err));
      
    fetch(`${API_BASE}/detections/latest`)
      .then(res => res.json())
      .then(data => setLatestDetection(data))
      .catch(err => console.error("Error loading latest detection:", err));


  }, []);

  const wikiUrl = (name) => `https://en.wikipedia.org/wiki/${name.replace(/ /g, "_")}`;
  const cornellUrl = (name) => `https://www.allaboutbirds.org/guide/${name.replace(/ /g, "_")}`;
  const speciesUrl = (name) => encodeURIComponent(name);

  return (
    <>
      <Header />
      
      <Container className="mt--7" fluid>
        {/* Stats Cards */}
        <Row className="gy-2 gx-2">
          <Col xs="12">
            <Card className="shadow-sm mb-4 xs-12">
              <CardBody className="py-3 px-4">
                <Row className="gy-3 gx-4">
                  {[
                    { label: "All Detections", value: dailyStats.total, icon: "info", color: "info" },
                    { label: "Today", value: dailyStats.today, icon: "sun", color: "success" },
                    { label: "Last Hour", value: dailyStats.lastHour, icon: "clock", color: "warning" },
                    { label: "Species Total", value: dailyStats.speciesTotal, icon: "feather-alt", color: "danger" },
                    { label: "Species Today", value: dailyStats.speciesToday, icon: "dove", color: "primary" },
                    { label: "Last Detection", value: dailyStats.lastDetection, icon: "music", color: "info" },
                  ].map((stat, i) => (
                    <Col key={i} xs="6" md="4" lg="2" className="d-flex align-items-center">
                      <div className="d-flex align-items-center w-100">
                        <div
                          className={`icon icon-shape bg-${stat.color} text-white rounded-circle d-flex align-items-center justify-content-center`}
                          style={{ width: "36px", height: "36px", marginRight: "0.75rem" }}
                        >
                          <i className={`fas fa-${stat.icon}`} style={{ fontSize: "0.9rem" }} />
                        </div>
                        <div className="text-truncate">
                          <div className="text-muted text-uppercase" style={{ fontSize: "0.7rem", lineHeight: "1rem" }}>
                            {stat.label}
                          </div>
                          <div className="font-weight-bold" style={{ fontSize: "1rem" }}>
                            {stat.value}
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </CardBody>
            </Card>
          </Col>

        </Row>

        {/* New Species Today, show only if there are new species today */}
        {newSpeciesToday.length === 0 && (
          <></>
        
        )}


        {/* New Species Detected Today Table */}
        {newSpeciesToday.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="shadow">
              <CardHeader>
                <h3>New Species Detected Today</h3>
              </CardHeader>
              <CardBody>
                <Table responsive>
                  <thead className="thead-light">
                    <tr>
                      <th>Common Name</th>
                      <th>Scientific Name</th>
                      <th>Max Confidence</th>
                      <th>First Detection</th>
                    </tr>
                  </thead>
                  <tbody>
                    {newSpeciesToday.map((bird, i) => (
                      <tr key={i}>
                        <td>
                          <a href={`/admin/species/${speciesUrl(bird.common)}`}>{bird.common}</a>{" "}
                          <a href={wikiUrl(bird.common)} target="_blank" rel="noopener noreferrer">
                            <i className="fab fa-wikipedia-w ml-2 text-muted"></i>
                          </a>
                          <a href={cornellUrl(bird.common)} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-binoculars ml-2 text-muted"></i>
                          </a>
                        </td>
                        <td><i>{bird.scientific}</i></td>
                        <td>{bird.confidence}</td>
                        <td>{bird.first}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
        )}

        {firstDetectionThisYear.length > 0 && (
        <Row className="mt-4">
          <Col md="12">
            <Card className="shadow">
              <CardHeader>
                <h3>Species Returned Today</h3>
              </CardHeader>
              <CardBody>
                <Table responsive>
                  <thead className="thead-light">
                    <tr>
                      <th>Common Name</th>
                      <th>Scientific Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {firstDetectionThisYear.slice(0, 10).map((bird, i) => (
                      <tr key={i}>
                        <td>
                          <a href={`/admin/species/${speciesUrl(bird.common)}`}>{bird.common}</a>
                        </td>
                        <td><i>{bird.scientific}</i></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
        )}

        <Row className="mt-5">
          <Col md="12">
            <h2>Latest detection: {dailyStats.lastDetection}</h2>
            {latestDetection && (
              <SpeciesCard species={latestDetection.common} />
            )}
            <CustomAudioPlayer onReady={() => setPlayersReady(prev => prev + 1)} />
          </Col>
        </Row>

        {/* Weather Card */}
        <Row className="mt-4">
          <Col md="12">
            <WeatherCard />
          </Col>
        </Row>

        {/* Species Activity Horizontal Bar Chart */}
        <Card className="shadow mt-5">
          <CardHeader>
            <h3>Today's Bird Activity</h3>
          </CardHeader>
          <CardBody>
            <Table responsive>
              <thead className="thead-light">
                <tr>
                  <th>Species</th>
                  <th>Detections</th>
                  <th>Activity</th>
                </tr>
              </thead>
              <tbody>
                {speciesActivity.labels.map((label, i) => {
                  const count = speciesActivity.datasets[0].data[i];
                  const max = Math.max(...speciesActivity.datasets[0].data);
                  const percent = Math.round((count / max) * 100);

                  return (
                    <tr key={i}>
                      <td>
                        <a href={`/admin/species/${encodeURIComponent(label)}`}>
                          {label}
                        </a>
                      </td>
                      <td>{count}</td>
                      <td>
                        <div style={{ background: "#eee", height: "12px", borderRadius: "6px" }}>
                          <div
                            style={{
                              width: `${percent}%`,
                              background: "#5e72e4",
                              height: "100%",
                              borderRadius: "6px",
                              transition: "width 0.3s ease-in-out",
                            }}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardBody>
        </Card>


        <Row>
          <Col md="12">
            <DetectionChart /> {/* uses today by default */}
          </Col>
        </Row>

      </Container>
    </>
  );
};

export default Index;
